// procedurePlateMatch — find the d-TPP plate that corresponds to a
// chosen CIFP procedure.
//
// Why this is non-trivial:
//   CIFP procedure idents are terse 6-char codes (CIITY3, ALWYS3, H10RZ)
//   while d-TPP plates have human-readable chartNames ("CIITY THREE
//   (RNAV)", "RNAV (RNP) Z RWY 10R"). The naming conventions for SIDs
//   and STARs are predictable; IAPs encode the approach type + runway
//   in the CIFP ident itself.
//
// SID / STAR matching: the CIFP ident is the chart-name root + a
// sequence number (CIITY3 → "CIITY THREE"). The plate's pdfName usually
// contains the bare root (00375CIITY.PDF), so we match by prefix
// against the pdfName instead of normalizing the spelled-out number.
//
// IAP matching: parse the CIFP ident to extract approach type letter
// (I/L/R/H/V/N/...) + runway, then match the plate chartName by
// approach-type label + runway suffix.

import type { PlateRecord } from './plates';
import type { RouteType } from './cifp/cifp';

interface MatchInputs {
  procIdent: string;
  routeType: RouteType;
  plates: PlateRecord[];
}

export function findPlateForProcedure({ procIdent, routeType, plates }: MatchInputs): PlateRecord | null {
  if (routeType === 'SID' || routeType === 'STAR') {
    return matchSidStar(procIdent, routeType, plates);
  }
  return matchIap(procIdent, plates);
}

// ── SID / STAR ───────────────────────────────────────────────────────────

// Hardcoded navaid-ident -> chartName-word table for old-format STAR/SIDs
// whose CIFP procIdent root is the navaid ident (e.g. 'JVL') while the
// FAA d-TPP plate chartName uses the navaid's spelled-out name
// ('JANESVILLE ONE'). Phase 3 added the same lookup on the Python side
// via /api/bbox navaid fetches; we keep a small hardcoded table here
// because the client doesn't have ready access to nationwide NASR data
// at runtime. The list covers the navaid-shorthand STAR/SIDs in our
// current corpus (KSFO, KSEA, KORD, KMSP, KEWR, KPHX, KSLC).
const NAVAID_SHORTHAND_EXPANSIONS: Record<string, string[]> = {
  // KSFO area
  BSR: ['BIGSUR', 'BIG SUR'],
  MOD: ['MODESTO'],
  PYE: ['POINTREYES', 'POINT REYES'],
  // KSEA / KSLC area
  OLM: ['OLYMPIA'],
  BVL: ['BONNEVILLE'],
  // KORD area
  JVL: ['JANESVILLE'],
  // KMSP / KEWR / KPHX area
  GEP: ['GOPHER'],
  FQM: ['FRANKLIN', 'WILLIAMSPORT'],
  BLH: ['BLYTHE'],
  // KSLC / KSEA backup
  HVE: ['HANKSVILLE'],
  PG: ['PAGE'],
};

function matchSidStar(procIdent: string, routeType: RouteType, plates: PlateRecord[]): PlateRecord | null {
  // CIFP idents end with a sequence number ('CIITY3', 'ALWYS3'). Strip
  // trailing digits to get the route root.
  const root = procIdent.replace(/\d+$/, '').trim();
  if (!root) return null;

  const targetChartCode = routeType === 'SID' ? 'DP' : 'STAR';
  const candidates = plates.filter((p) => p.chartCode === targetChartCode);

  // Match by pdfName — most reliable because the pdfName embeds the
  // chart's short ident without the spelled-out number.
  const upperRoot = root.toUpperCase();
  const byPdf = candidates.find((p) => p.pdfName.toUpperCase().includes(upperRoot));
  if (byPdf) return byPdf;

  // Fallback: try matching by chartName prefix (e.g. 'CIITY THREE' starts with 'CIITY').
  const byChartName = candidates.find((p) => p.chartName.toUpperCase().startsWith(upperRoot));
  if (byChartName) return byChartName;

  // Old-format navaid-shorthand fallback. If the root is a known navaid
  // ident, try its spelled-out variants in both pdfName and chartName.
  const expansions = NAVAID_SHORTHAND_EXPANSIONS[upperRoot];
  if (expansions) {
    for (const exp of expansions) {
      const byPdfExp = candidates.find((p) => p.pdfName.toUpperCase().includes(exp));
      if (byPdfExp) return byPdfExp;
      const byChartNameExp = candidates.find((p) => p.chartName.toUpperCase().startsWith(exp));
      if (byChartNameExp) return byChartNameExp;
    }
  }

  return null;
}

// ── IAP ──────────────────────────────────────────────────────────────────

interface ParsedIapIdent {
  approachType: string;          // 'ILS', 'LOC', 'RNAV', 'RNAV (RNP)', 'VOR', 'NDB', 'TACAN'
  runway: string;                 // '10L', '28R', '19', etc.
  variant: string | null;         // 'Y', 'Z', or null
}

/** Parse CIFP IAP ident into approach type + runway + variant.
 *
 * CIFP IAP idents look like:
 *   I28L      → ILS RWY 28L
 *   L28L      → LOC RWY 28L
 *   R10R      → RNAV RWY 10R
 *   R10RY     → RNAV Y RWY 10R
 *   R28RZ     → RNAV Z RWY 28R
 *   H10RZ     → RNAV (RNP) Z RWY 10R
 *   V31       → VOR RWY 31
 *   N18       → NDB RWY 18
 *   D31       → VOR/DME RWY 31
 *   B25       → LOC BC RWY 25 (back course)
 *
 * The first character is the approach-type letter.
 */
export function parseIapIdent(procIdent: string): ParsedIapIdent | null {
  if (procIdent.length < 2) return null;
  const typeChar = procIdent[0].toUpperCase();
  // FAA's CIFP encoding sometimes emits the variant separator as a hyphen
  // (e.g. 'R14-Y' for RNAV (GPS) Y RWY 14, 'H10R-Z' for RNAV (RNP) Z RWY
  // 10R). The hyphen is stylistic; strip it so the runway + variant parse
  // produces the same result as the hyphen-less form.
  const rest = procIdent.substring(1).replace(/-/g, '');

  // Trailing letter Y or Z is the variant (always last).
  let runway = rest;
  let variant: string | null = null;
  if (rest.length >= 2 && /[YZ]$/i.test(rest)) {
    variant = rest.slice(-1).toUpperCase();
    runway = rest.slice(0, -1);
  }
  runway = runway.replace(/^0+/, '') || runway; // Strip leading zeros, but keep '0' itself if that's all there is.
  if (!runway) return null;

  let approachType = '';
  switch (typeChar) {
    case 'I': approachType = 'ILS'; break;
    case 'L': approachType = 'LOC'; break;
    case 'R': approachType = 'RNAV'; break;
    case 'H': approachType = 'RNAV (RNP)'; break;
    case 'V': approachType = 'VOR'; break;
    case 'N': approachType = 'NDB'; break;
    case 'D': approachType = 'VOR/DME'; break;
    case 'B': approachType = 'LOC BC'; break;
    case 'S': approachType = 'GPS'; break;
    case 'X': approachType = 'LDA'; break;
    case 'Y': approachType = 'TACAN'; break;
    case 'Q': approachType = 'NDB DME'; break;
    case 'P': approachType = 'GPS'; break;
    case 'G': approachType = 'IGS'; break;
    default:  return null;
  }
  return { approachType, runway, variant };
}

function matchIap(procIdent: string, plates: PlateRecord[]): PlateRecord | null {
  const parsed = parseIapIdent(procIdent);
  if (!parsed) return null;

  const iaps = plates.filter((p) => p.chartCode === 'IAP');
  const rwyTokens = [`RWY ${parsed.runway}`, ` ${parsed.runway} `, `RWY${parsed.runway}`];

  // Score each candidate: needs matching approach type + matching runway,
  // plus the variant letter when one was parsed.
  let best: { plate: PlateRecord; score: number } | null = null;
  for (const p of iaps) {
    const name = p.chartName.toUpperCase();
    let score = 0;

    // Approach type — match the longest label first ('RNAV (RNP)' before 'RNAV').
    if (parsed.approachType === 'RNAV (RNP)' && /RNAV\s*\(RNP\)/.test(name)) score += 100;
    else if (parsed.approachType === 'RNAV' && /\bRNAV\b/.test(name) && !/\(RNP\)/.test(name)) score += 100;
    else if (parsed.approachType === 'ILS' && /\bILS\b/.test(name)) score += 100;
    else if (parsed.approachType === 'LOC BC' && /\bLOC\s*BC\b/.test(name)) score += 100;
    else if (parsed.approachType === 'LOC' && /\bLOC\b/.test(name) && !/\bBC\b/.test(name)) score += 80;
    else if (parsed.approachType === 'VOR/DME' && /VOR\/DME/.test(name)) score += 100;
    else if (parsed.approachType === 'VOR' && /\bVOR\b/.test(name)) score += 80;
    else if (parsed.approachType === 'NDB' && /\bNDB\b/.test(name)) score += 100;
    else if (parsed.approachType === 'LDA' && /\bLDA\b/.test(name)) score += 100;
    else if (parsed.approachType === 'TACAN' && /\bTACAN\b/.test(name)) score += 100;
    else if (parsed.approachType === 'GPS' && /\bGPS\b/.test(name)) score += 100;
    else continue;

    // Runway match — try precise tokens.
    let runwayHit = false;
    for (const t of rwyTokens) {
      if (name.includes(t)) { runwayHit = true; break; }
    }
    if (!runwayHit) continue;
    score += 50;

    // Variant (Y/Z) when present.
    if (parsed.variant) {
      const re = new RegExp(`\\b${parsed.variant}\\b\\s+RWY`);
      if (re.test(name)) score += 20;
    } else {
      // Prefer plates WITHOUT a Y/Z variant when the CIFP ident has none.
      if (!/\b[YZ]\b\s+RWY/.test(name)) score += 10;
    }

    if (!best || score > best.score) best = { plate: p, score };
  }
  return best?.plate ?? null;
}

// ── INVERSE DIRECTION: plate → procedure ─────────────────────────────────────
//
// Given a plate's chartName (human-readable, e.g. "RNAV (GPS) Y RWY 14"),
// find the matching CIFP procedure record from a procedure list.
//
// Used by PlateOverlayLayer to attach holdings + procedure-turn rendering
// to a plate. The naive pdfName.includes(procIdent) heuristic fails when
// CIFP uses hyphenated idents like 'R14-Y' that don't appear in the
// FAA d-TPP pdfName '00251RY14.PDF'. This scored matcher mirrors the
// Python `match_plate_to_procedure` used server-side in plate_georef.py.

export interface ProcedureLike {
  procIdent: string;
  routeType: string;
}

export interface FindProcedureForPlateInputs<P extends ProcedureLike> {
  chartName: string;
  procedures: P[];
}

/**
 * Score every IAP procedure against the plate's chartName + parsed
 * `procIdent` and return the highest-scoring match. Returns null when no
 * procedure can be plausibly tied to this plate (score 0).
 *
 * Scoring rubric (mirrors plate_georef.py match_plate_to_procedure):
 *   - approach type match           : +100 (or +80 for less-specific LOC/VOR)
 *   - runway suffix match           : +50 (must hit; otherwise candidate is
 *                                          rejected)
 *   - variant match (Y/Z)           : +20 when explicit; -30 when the
 *                                     chartName has a variant but the
 *                                     procIdent doesn't (mismatch).
 *   - circling suffix on procIdent  : +60 when chartName ends with the
 *                                     same letter (-A / -B / -C / -D).
 *
 * GLS plates (chartName contains 'GLS') fall back to the ILS or RNAV
 * procedure for the same runway with a slightly lower score so that any
 * real ILS or RNAV plate's match still wins.
 */
export function findProcedureForPlate<P extends ProcedureLike>(
  { chartName, procedures }: FindProcedureForPlateInputs<P>,
): P | null {
  const upper = chartName.toUpperCase();
  const iaps = procedures.filter((p) => p.routeType === 'IAP');
  const isGlsPlate = /\bGLS\b/.test(upper);

  let best: { proc: P; score: number } | null = null;
  for (const proc of iaps) {
    const parsed = parseIapIdent(proc.procIdent);
    if (!parsed) continue;
    let score = 0;
    const at = parsed.approachType;

    if (at === 'RNAV (RNP)' && /RNAV\s*\(RNP\)/.test(upper)) score += 100;
    else if (at === 'RNAV' && /\bRNAV\b/.test(upper) && !/\(RNP\)/.test(upper)) score += 100;
    else if (at === 'ILS' && /\bILS\b/.test(upper)) score += 100;
    else if (at === 'LOC BC' && /\bLOC\b[^A-Z]*\bBC\b/.test(upper)) score += 100;
    else if (at === 'LOC' && /\bLOC\b/.test(upper) && !/\bBC\b/.test(upper)) score += 80;
    else if (at === 'VOR/DME' && /VOR\/DME/.test(upper)) score += 100;
    else if (at === 'VOR' && /\bVOR\b/.test(upper)) score += 80;
    else if (at === 'NDB' && /\bNDB\b/.test(upper)) score += 100;
    else if (at === 'LDA' && /\bLDA\b/.test(upper)) score += 100;
    else if (at === 'TACAN' && /\bTACAN\b/.test(upper)) score += 100;
    else if (at === 'GPS' && /\bGPS\b/.test(upper)) score += 100;
    else if (isGlsPlate && at === 'ILS') score += 75;     // GLS-on-ILS fallback
    else if (isGlsPlate && at === 'RNAV') score += 70;    // GLS-on-RNAV fallback
    else continue;

    // Runway match. Required — a procedure with no runway match cannot
    // claim this plate.
    const rwy = parsed.runway;
    if (!rwy) continue;
    const rwyPadded = rwy.length >= 2 ? rwy : `0${rwy}`;
    const rwyUnpadded = rwy.replace(/^0+/, '') || rwy;
    const tokens = new Set<string>();
    for (const r of [rwy, rwyPadded, rwyUnpadded]) {
      tokens.add(`RWY ${r}`);
      tokens.add(` ${r} `);
      tokens.add(`RWY${r}`);
    }
    let runwayHit = false;
    for (const t of tokens) {
      if (upper.includes(t)) { runwayHit = true; break; }
    }
    if (!runwayHit) continue;
    score += 50;

    // Variant match.
    if (parsed.variant) {
      const re = new RegExp(`\\b${parsed.variant}\\b\\s+RWY`);
      if (re.test(upper)) score += 20;
    } else if (/\b[WXYZ]\b\s+RWY/.test(upper)) {
      // chartName has a variant but procIdent doesn't — strong mismatch.
      score -= 30;
    }

    if (!best || score > best.score) best = { proc, score };
  }
  return best?.proc ?? null;
}
